import { Component, Input, OnInit } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { BehaviorSubject, EMPTY, Observable } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/store/reducers";
import { setSnackbar } from "src/app/store/actions/messages/message.actions";

import { ProductSuppliersService } from "src/app/shared/services/products/productSuppliers/productSuppliers.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { ProductSupplierDTO } from "src/app/models/Products/productSupplierDTO";
import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  ExportEvent,
  FieldType,
  FilterEvent,
  FilterName,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { setAdvancedFilter } from "src/app/shared/utils/tableUtils";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";

import { ProductSuppliersDialogComponent } from "../dialogs/product-suppliers/product-suppliers-dialog.component";

const USAGE_CODE = "PRODUCT.SUPPLIERS";

@Component({
  selector: "product-suppliers-list",
  templateUrl: "./product-suppliers-list.component.html",
  styleUrls: ["./product-suppliers-list.component.scss"],
})
export class ProductSuppliersListComponent {
  @Input() set productCode(code: string) {
    if (!code) {
      return;
    }
    this._productCode = code;
    this.setTableTitle();
    this.setDialogData();
    this.getTableConfig();
    this.getProductSuppliersList(
      undefined, 
      this.defaultOrderingQuery(),
    );
  }
  @Input() permissionResource: string;

  _productCode: string;

  isLoading$ = new BehaviorSubject<boolean>(false);
  totalProductSuppliers$ = new BehaviorSubject<number>(0);
  columnInfo$: Observable<ColumnSorterInfo[]>;

  productSuppliersList$: Observable<ProductSupplierDTO[]>;
  excelProductSuppliersList$: Observable<ProductSupplierDTO[]>;

  tableTitle: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Products.partySupplierCode",
      field: "partySupplierCode",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Products.partySupplierName",
      field: "partySupplierName",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Products.token",
      field: "token",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.defaultSupplier",
      field: "defaultSupplier",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "Products.productSupplierRefCode",
      field: "productSupplierRefCode",
      fieldType: FieldType.STRING,
      identifier: true,
      searchable: true,
    },
    {
      header: "Products.minBuyQuantity",
      field: "minBuyQuantity",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "Products.lastBuyDate",
      field: "lastBuyDate",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      noOffset: true
    },
    {
      header: "Products.lastBuyAmount",
      field: "lastBuyAmount",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "Products.createdOn",
      field: "createdOn",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.STRING,
      hidden: true,
    },
  ];

  productSuppliersDialogComponent = ProductSuppliersDialogComponent;
  productSuppliersDialogData: object;

  // loaded locale
  public locale: string = localStorage.getItem("locale");

  constructor(
    private store: Store<AppState>, 
    private translate: TranslateService,
    private tableConfigService: TableConfigService,
    private productSuppliersService: ProductSuppliersService,
  ) { }

  setTableTitle() {
    this.tableTitle = this.translate.instant("Products.productSuppliers");
  }

  setDialogData() {
    this.productSuppliersDialogData = {
      productCode: this._productCode,
    }
  }

  getProductSuppliersList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);

    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.productSuppliersList$ = this.productSuppliersService
      .getProductSuppliersList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        advancedFilter
      )
      .pipe(
        map((result) => {
          const { items, size } = result;
          this.totalProductSuppliers$.next(size);
          return items.map((item) => ({ ...item, productDescription: item.productDescription[this.locale] }));
        }),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("Products.productSuppliersLoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  getProductSuppliersExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);

    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.excelProductSuppliersList$ = this.productSuppliersService
      .getProductSuppliersList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
        },
        searchEvent,
        sortEvent,
        advancedFilter
      )
      .pipe(
        map(({ items }) => items.map((item) => ({ ...item, productDescription: item.productDescription[this.locale] }))),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("Products.productSuppliersLoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  setAdvancedFilter(filterEvent: FilterEvent) {
    const productSupplierFilter = {
      column: "productCode",
      operator: FilterName.EQUALS_TO,
      value: this._productCode,
    };

    return setAdvancedFilter(productSupplierFilter, filterEvent);
  }

  private defaultOrderingQuery(): SortEvent {
    return {
      active: 'defaultSupplier',
      direction: 'desc',
    }
  }

  getTableConfig() {
    this.columnInfo$ = this.tableConfigService
      .getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.LoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getProductSuppliersList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onGetExportData(exportEvent: ExportEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    this.getProductSuppliersExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.isLoading$.next(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            )
          )
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }
}
